<template>
    <div v-if="linkVerified" class="page-404-bg">
        <div class="wrapper-pages pt-2 pt-sm-5 pb-4 pb-sm-5 position-relative">
            <div class="container">
                <div class="row justify-content-center">
                    <div class="col-xxl-4 col-lg-5">
                        <div class="brand-logo mb-5 text-center">
                            <a href="index.html">
                                <router-link to="/login">
                                    <img :src="`${$logoDir}logo-auth.png`" alt="HAXO" height="72" class="img-fluid">
                                </router-link>
                            </a>
                        </div>

                        <div class="card">
                            <div class="card-body p-4">
                                <div class="text-center">
                                    <h1 class="text-error">{{ $t('common.welcome') }}</h1>
                                    <p class="text-white mt-3">{{ $t('adminCreation.subtitle1') }}</p>
                                    <p class="text-white mt-3">{{ $t('adminCreation.subtitle2') }}</p>
                                </div>
                                <form @submit.prevent="handleSubmit" class="pb-4" autocomplete="off">
                                    <div class="my-auto text-white mb-3">
                                        <div class="mb-2">
                                            <label for="name" class="form-label">{{ $t('common.lastName') }}</label>
                                            <input class="form-control" v-model="name" type="text" required maxlength="32" :placeholder="$t('member.lastNamePlaceholder')">
                                        </div>
                                        <div class="mb-2">
                                            <label for="first-name" class="form-label">{{ $t('common.firstName') }}</label>
                                            <input class="form-control" v-model="first_name" type="text" required maxlength="32" :placeholder="$t('member.firstNamePlaceholder')">
                                        </div>
                                        <div class="mb-2">
                                            <label for="mobile" class="form-label">{{ $t('common.mobilePhone') }}</label>
                                            <vue-tel-input class="form-control" v-model="mobile_phone_number" @validate="onValidPhoneNumber" :validCharactersOnly="true" :inputOptions="{required:true, placeholder:$t('account.phonePlaceholder'), id:'phone-input'}"></vue-tel-input>
                                        </div>
                                        <div class="mb-2">
                                            <PasswordComponent id="password" :label="$t('changepwd.newPassword')" :displayHints="true" v-model="password" />
                                        </div>
                                        <div class="mb-2">
                                            <PasswordComponent id="confirmed-password" :label="$t('changepwd.confirmPassword')" v-model="confirmed_password" />
                                        </div>
                                        <div class="mb-2">
                                            <div class="form-check">
                                                <input type="checkbox" v-model="cguAccepted" class="form-check-input" id="checkbox-cgu">
                                                {{ $t('common.termsAgreement') }} <a href="/cgu-managers" target="_blank" rel="noopener noreferrer" style="color: white; text-decoration: underline;">{{ $t('common.termsOfUse') }}</a>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="d-grid mb-0 text-center">
                                        <button class="btn btn-info" type="submit" :disabled="!cguAccepted"><i class="mdi mdi-check"></i> {{ $t('common.submit') }}</button>
                                    </div>
                                    <div v-if="errorMessage" class="mt-2 text-white">
                                        {{ errorMessage }}
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <footer class="footer footer-alt text-white">
            {{ year }} {{ $t('common.copyright') }}
        </footer>
    </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useUserStore } from '@/stores/userStore';
import { useI18n } from 'vue-i18n';
import PasswordComponent from '@/components/password';

export default {
    name: 'AdminCreation',
    components: {
        PasswordComponent,
    },
    setup() {
        const router = useRouter();
        const route = useRoute();
        const userStore = useUserStore();
        const { t } = useI18n();
        const year = new Date().getFullYear();
        const name = ref(null);
        const first_name = ref(null);
        const mobile_phone_number = ref('');
        const password = ref('');
        const confirmed_password = ref('');
        const cguAccepted = ref(false);
        const uuid = route.query.uuid;
        const errorMessage = ref(null);
        const linkVerified = ref(false);
        const validPhoneNumber = ref(false);

        if(uuid === undefined) {
            router.push({ path: "/404"});
        }

        onMounted(async () => {
            try {
                await userStore.checkUUID(uuid, 'facility_configuration');
                linkVerified.value = true;
                document.title = 'HAXO - Espace Gestionnaires'
            } catch (error) {
                router.push({ path: "/result", query: { success: false, title: t('common.failure'), message: error }});
            }
        });

        const handleSubmit = async () => {
            if(validate()) {
                await createManagerAccount();
            }
        };

        const onValidPhoneNumber = ({ valid }) => {
            validPhoneNumber.value = valid;
            if(valid) {
                const input = document.getElementById('phone-input');
                if(input) {
                    input.setCustomValidity('');
                }
            }
        };

        const validate = () => {
            displayError('');
            const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,32}$/;
            if (!passwordRegex.test(password.value)) {
                displayError(t('common.passwordRequirements'));
                return false;
            }
            if(password.value != confirmed_password.value) {
                displayError(t('common.passwordsMustMatch'));
                return false;
            }
            if(!validPhoneNumber.value) {
                displayError(t('common.phoneInvalid'));
                return false;
            }
            return true;
        };

        const displayError = (error) => {
            errorMessage.value = error;
        }

        const createManagerAccount = async () => {
            try {
                const domainPrefix = await userStore.configureFacility(uuid, name.value, first_name.value, password.value, mobile_phone_number.value.replaceAll(' ', ''), cguAccepted.value);
                errorMessage.value = "";
                router.push({ path: "/admin-creation-success", query: { prefix: domainPrefix } });
            } catch (error) {
                errorMessage.value = error;
            }
        }

        return {
            year,
            name,
            first_name,
            mobile_phone_number,
            password,
            confirmed_password,
            handleSubmit,
            errorMessage,
            cguAccepted,
            linkVerified,
            onValidPhoneNumber,
        }
    }
}
</script>

<style scoped>
.page-404-bg {
    background: rgb(45, 64, 140);
    background: linear-gradient(164deg, rgb(32, 123, 194) 0%, rgb(45, 64, 140) 50%, rgb(114, 44, 132) 100%);
}

.page-404-bg .wrapper-pages {
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    min-height: 100vh;
}

.page-404-bg .card {
    background: rgba(255, 255, 255, 0.11);
    border-radius: 16px;
    -webkit-box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(5px);
    -webkit-backdrop-filter: blur(5px);
    border: 1px solid rgba(255, 255, 255, 0.3);
}

.page-404-bg .text-error {
    color: #fff !important;
    text-shadow: rgba(255, 255, 255, 0.3) 5px 1px, rgba(255, 255, 255, 0.2) 10px 3px;
    font-size: 52px;
}

:deep(.vue-tel-input.form-control) {
    display: flex;
}

:deep(.vue-tel-input.form-control .vti__input) {
    color: var(--ct-primary-color);
}

:deep(.vue-tel-input.form-control .vti__dropdown) {
    padding: 0;
}

:deep(.vue-tel-input.form-control .vti__input::placeholder) {
    color: var(--ct-secondary-color);
    opacity: 1;
}

</style>
